import _ from 'underscore';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { IMainScope } from '../../app/IMainScope';
import { ServerUrls } from '../../app/ServerUrls';
import { UpdateScheduleFrequencyServerSettingNames } from '../../backgroundJob/types/UpdateScheduleFrequencyServerSettingRequest';
import { Messages } from '../../codegen/Localize';
import { HelpService } from '../../commonInfra/services/HelpService';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { FrequencyOption, IScheduleFrequencyViewModel } from '../../ts/controllers/CreateOrEditScheduleCtrl';
import { CreateOrEditScheduleViewModelMapper } from '../../ts/controllers/CreateOrEditScheduleViewModelMapper';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { Module } from '../../ts/Module';
import { LanguagesInstance } from '../../ts/resources/Languages';
import { LocalesInstance } from '../../ts/resources/Locales';
import { ClearSavedPasswordsRequest } from '../../ts/ServerApi/requests/ClearSavedPasswordsRequest';
import { GetTotalRefreshTokenCountRequest, IGetTokenResult } from '../../ts/ServerApi/requests/GetTotalRefreshTokenCountRequest';
import { ResetServerSettingsRequest } from '../../ts/ServerApi/requests/ResetServerSettingsRequest';
import { RunActiveDirectoryGroupsSyncRequest } from '../../ts/ServerApi/requests/RunActiveDirectoryGroupsSyncRequest';
import { RunRecommendationsTrainerRequest } from '../../ts/ServerApi/requests/RunRecommendationsTrainerRequest';
import { RunViewRecommendationsTrainerRequest } from '../../ts/ServerApi/requests/RunViewRecommendationsTrainerRequest';
import { UpdateBooleanServerSettingNames } from '../../ts/ServerApi/requests/UpdateBooleanServerSettingRequest';
import { UpdateStringServerSettingNames } from '../../ts/ServerApi/requests/UpdateStringServerSettingRequest';
import { IEditableServerSettings } from '../../ts/ServerApi/types/IEditableServerSettings';
import { ILanguage } from '../../ts/ServerApi/types/ILanguage';
import { ILocale } from '../../ts/ServerApi/types/ILocale';
import { ConfirmActionDialog } from '../../ts/services/ConfirmActionDialog';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { UserSettingsActions } from '../../userSettings/services/UserSettingsActions';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { IServerTimestamps, ServerSettings, ServerSettingsInstance } from '../services/ServerSettings';
import { ServerSettingsUnauthenticated } from '../services/ServerSettingsUnauthenticated';
import { StartPageService } from '../services/StartPageService';

export interface IServerSettingsScope extends IMainScope {
  serverSettingsOptions: IEditableServerSettings;  // Resolved by state provider
  serverTimestamps: IServerTimestamps; // resolved by state provider
}

export interface IServerSettingsFormScope extends IServerSettingsScope, ISettingsCoordinatorCtrlScope {
  serverSettingsForm: ng.IFormController; // name comes from pug

  clearSavedCredentials: () => void;
  updateDefaultLanguage: (value: string) => void;
  updateDefaultLocale: (value: string) => void;
  serverSettingsPending: boolean;
  server: SessionManagementService;
  languages: ILanguage[];
  languageDisplayNames: _.Dictionary<string>;
  locales: ILocale[];
  localeDisplayNames: _.Dictionary<string>;
  isScheduledADSyncAllowed: boolean;
  isRecommendationsEnabled: boolean;
  isVizRecommendationsEnabled: boolean;
  runActiveDirectoryGroupsSync: () => void;
  runRecommendationsTrainer: () => void;
  activeDirectorySyncScheduleViewModel: IScheduleFrequencyViewModel;
  recommendationsTrainerScheduleViewModel: IScheduleFrequencyViewModel;
  totalRefreshTokenCount: number;
  refreshTokenUncheckedMessage: string;
  viewUpdateHref: string;
  learnMoreHref: string;
  startPage: string;
  defaultStartPage: string;
  resetServerStartPage: () => void;
  setSandboxSettingForExistingSites: (value: boolean) => void;
}

const frequencyOptionsForRecommendationsTrainerSchedule = [
  FrequencyOption.Daily,
  FrequencyOption.Weekly,
  FrequencyOption.Monthly
];

export class ServerSettingsFormCtrl {
  private ServerSettings: ServerSettings;
  private masterSettings: IEditableServerSettings;
  private originalActiveDirectorySyncScheduleViewModel: IScheduleFrequencyViewModel;
  private originalRecommendationsTrainerScheduleViewModel: IScheduleFrequencyViewModel;
  private languageOrLocaleChanged: boolean;
  private server: SessionManagementService;

  private toasterService = ToasterService.instance;

  public static $inject = [
    '$scope',
    '$q',
    '$translate',
    'SettingsFormService',
    'ConfirmActionDialog',
  ];

  constructor(
    private scope: IServerSettingsFormScope,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
            formService: SettingsFormService,
    private confirmActionDialog: ConfirmActionDialog,
  ) {
    this.server = getSessionManagementServiceInstance();
    this.languageOrLocaleChanged = false;
    this.scope.serverSettingsPending = false;
    this.ServerSettings = ServerSettingsInstance;
    this.scope.server = this.server;
    this.scope.updateDefaultLanguage = (value: string) => this.scope.serverSettingsOptions.defaultLanguage = value;
    this.scope.updateDefaultLocale = (value: string) => this.scope.serverSettingsOptions.defaultLocale = value;
    this.scope.clearSavedCredentials = () => this.clearSavedCredentials();
    this.scope.isScheduledADSyncAllowed = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
    this.scope.isRecommendationsEnabled = this.server.isRecommendationsEnabled();
    this.scope.isVizRecommendationsEnabled = this.server.isVizRecommendationsEnabled();
    this.scope.runActiveDirectoryGroupsSync = () => this.runActiveDirectoryGroupsSync();
    this.scope.runRecommendationsTrainer = () => this.runRecommendationsTrainer();
    this.scope.viewUpdateHref = encodeURI(ServerUrls.getInstance().serverStatusVizUrl('BackgroundTasksforNonExtracts'));
    this.scope.learnMoreHref = HelpService.getServerHelpUrlByCategory('settings-adsync1');
    this.scope.activeDirectorySyncScheduleViewModel = CreateOrEditScheduleViewModelMapper.viewModelFromScheduleFrequency(this.scope.serverSettingsOptions.activeDirectorySyncScheduleFrequency);
    this.originalActiveDirectorySyncScheduleViewModel = angular.copy(this.scope.activeDirectorySyncScheduleViewModel);
    this.scope.recommendationsTrainerScheduleViewModel = CreateOrEditScheduleViewModelMapper.viewModelFromScheduleFrequency(this.scope.serverSettingsOptions.recommendationsTrainerScheduleFrequency,
      frequencyOptionsForRecommendationsTrainerSchedule);
    this.originalRecommendationsTrainerScheduleViewModel = angular.copy(this.scope.recommendationsTrainerScheduleViewModel);

    this.scope.setSandboxSettingForExistingSites = (value: boolean) => this.setSandboxSettingForExistingSites(value);

    this.scope.sharedForm = formService;
    this.scope.localForm=  {
      formController: this.scope.serverSettingsForm,
      save: () => this.save(),
      cancel: () =>  {
        this.scope.serverSettingsOptions = angular.copy(this.masterSettings);
        this.scope.activeDirectorySyncScheduleViewModel = angular.copy(this.originalActiveDirectorySyncScheduleViewModel);
        this.scope.recommendationsTrainerScheduleViewModel = angular.copy(this.originalRecommendationsTrainerScheduleViewModel);
      },
      isBusy: () => this.scope.serverSettingsPending,
      isFormUnchanged: () => {
        return angular.equals(this.scope.serverSettingsOptions, this.masterSettings)
            && angular.equals(this.scope.activeDirectorySyncScheduleViewModel, this.originalActiveDirectorySyncScheduleViewModel)
            && angular.equals(this.scope.recommendationsTrainerScheduleViewModel, this.originalRecommendationsTrainerScheduleViewModel);
      },
      onSave: () => this.hardReloadIfNeeded(),
      resetSettings: () => this.resetSettings()
    };

    this.scope.defaultStartPage = StartPageService.getServerDefaultStartPage();
    this.scope.startPage = this.getServerStartPage(this.scope.serverSettingsOptions.defaultStartPage);
    this.scope.resetServerStartPage = () => this.resetServerStartPage();
    this.masterSettings = angular.copy(this.scope.serverSettingsOptions);

    this.getLanguages();
    this.getLocales();
    this.getTotalRefreshTokenCount();
  }

  private getLanguages() {
    LanguagesInstance.getAll()
      .then((languages: ILanguage[]) => {
        this.scope.languages = languages;
        this.scope.languageDisplayNames = {};
        _.each(languages, (language: ILanguage) => {
          this.scope.languageDisplayNames[language.language] = language.displayName;
        });
      });
  }

  private getLocales() {
    LocalesInstance.getAll()
      .then((locales: ILocale[]) => {
        this.scope.locales = locales;
        this.scope.localeDisplayNames = {};
        _.each(locales, (locale: ILocale) => {
          this.scope.localeDisplayNames[locale.locale] = locale.displayName;
        });
      });
  }

  private resetSettings() {
    this.confirmActionDialog.open({
      title: this.$translate.instant('resetServerSettings'),
      bodyText: this.$translate.instant('resetServerSettingsConfirmMessage'),
      actionName: this.$translate.instant('resetServerSettingsConfirmButton'),
      isDeleteAction: true,
      action: () => {
        return WebHttpClient.sendRequest(new ResetServerSettingsRequest())
          .then(() => this.ServerSettings.fetch())
          .then((settings: IEditableServerSettings) => {
            this.scope.serverSettingsOptions = settings;
            this.languageOrLocaleChanged = this.isLangOrLocaleChanged();
            this.masterSettings = angular.copy(this.scope.serverSettingsOptions);
            this.scope.recommendationsTrainerScheduleViewModel = CreateOrEditScheduleViewModelMapper.viewModelFromScheduleFrequency(settings.recommendationsTrainerScheduleFrequency,
              frequencyOptionsForRecommendationsTrainerSchedule);
          })
          .then(() => this.hardReloadIfNeeded());
          // TODO: handle error, message this.$translate.instant('resetServerSettingsError')
      }
    }).result.then(() => this.toasterService.pop({ type: ToastType.Info, text: this.$translate.instant('resetServerSettingsSuccess') }));
  }

  private getTotalRefreshTokenCount(): void {
    WebHttpClient.sendRequest(new GetTotalRefreshTokenCountRequest({}))
      .then((result: IGetTokenResult) => {
        this.scope.totalRefreshTokenCount = result.refreshTokenCount;
        this.scope.refreshTokenUncheckedMessage = this.$translate.instant('serverConnectedDevicesUncheckedWithCount',
          { TOKEN_COUNT: this.scope.totalRefreshTokenCount}, L10n.Interpolations.Count);
      }
    );
  }

  private clearSavedCredentials(): void {
    this.confirmActionDialog.open({
      title: this.$translate.instant('clearSavedCredentials'),
      bodyText: this.$translate.instant('clearServerSavedCredentialsConfirmMessage'),
      actionName: this.$translate.instant('clearSavedCredentialsConfirmButton'),
      isDeleteAction: true,
      action: () => {
        return WebHttpClient.sendRequest(new ClearSavedPasswordsRequest());
        // TODO: handle error, message this.$translate.instant('clearSavedCredentialsError')
      }
    }).result.then(() => this.toasterService.pop({ type: ToastType.Info, text: this.$translate.instant('clearSavedCredentialsSuccess') }));
  }

  private setSandboxSettingForExistingSites(value: boolean): void {
    let title: string;
    let bodyText: string;
    let successMessage: string;

    if(value) {
      title = Messages.sandboxExistingSitesEnableHeader();
      bodyText = Messages.sandboxExistingSitesEnableMessage();
      successMessage = Messages.sandboxExistingSitesEnableSuccessMessage();
    } else {
      title = Messages.sandboxExistingSitesDisableHeader();
      bodyText = Messages.sandboxExistingSitesDisableMessage();
      successMessage = Messages.sandboxExistingSitesDisableSuccessMessage();
    }

    this.confirmActionDialog.open({
      title: title,
      bodyText: bodyText,
      actionName: title,
      action: () => {
        return this.ServerSettings.setSandboxSettingForExistingSites(value);
      }
    }).result.then(() => this.toasterService.pop({ type: ToastType.Info, text: successMessage}));
  }

  private save(): ng.IPromise<any> {
    this.scope.serverSettingsPending = true;

    // The settings we'll save. Copy them, because we'll want to set
    // them to the masterSettings if we successfully save
    const settings = angular.copy(this.scope.serverSettingsOptions);
    const booleanNames = UpdateBooleanServerSettingNames;
    const stringNames = UpdateStringServerSettingNames;
    const embeddedCredentialsAndSchedulingPromise =
      this.ServerSettings.updateBoolean(booleanNames.embeddedCredentialsEnabled, settings.embeddedCredentialsEnabled)
        .then(() => {
          // Since scheduling depends on embeddedCredentials being enabled, make sure embeddedCredentials
          // saves first and only save scheduling if embeddedCredentials is enabled
          if (settings.embeddedCredentialsEnabled) {
            return this.ServerSettings.updateBoolean(booleanNames.schedulingEnabled, settings.schedulingEnabled);
          }
        });

    const promises = [
      embeddedCredentialsAndSchedulingPromise,
      this.ServerSettings.updateBoolean(booleanNames.savedPasswordsEnabled, settings.savedPasswordsEnabled),
      this.ServerSettings.updateBoolean(booleanNames.savedAccessTokensEnabled, settings.savedAccessTokensEnabled),
      this.ServerSettings.updateString(stringNames.defaultLanguage, settings.defaultLanguage),
      this.ServerSettings.updateString(stringNames.defaultLocale, settings.defaultLocale),
      this.ServerSettings.updateBoolean(booleanNames.refreshTokensEnabled, settings.refreshTokenEnabled),
      this.ServerSettings.updateBoolean(booleanNames.personalSandboxEnabled, settings.personalSandboxEnabled),
      this.ServerSettings.updateBoolean(booleanNames.personalSandboxByDefaultEnabled, settings.personalSandboxByDefaultEnabled),
      this.ServerSettings.updateString(stringNames.signInNoteText, settings.signInNoteText),
      this.ServerSettings.updateString(stringNames.signInNoteUrl, settings.signInNoteUrl)
    ];

    promises.push(this.ServerSettings
      .updateBoolean(booleanNames.scheduledActiveDirectorySyncEnabled, settings.scheduledActiveDirectorySyncEnabled));
    promises.push(this.ServerSettings
      .updateScheduleFrequency(UpdateScheduleFrequencyServerSettingNames.activeDirectorySyncScheduleFrequency,
        CreateOrEditScheduleViewModelMapper.scheduleFrequencyFromViewModel(this.scope.activeDirectorySyncScheduleViewModel)));
    promises.push(this.ServerSettings
      .updateScheduleFrequency(UpdateScheduleFrequencyServerSettingNames.recommendationsTrainerScheduleFrequency,
        CreateOrEditScheduleViewModelMapper.scheduleFrequencyFromViewModel(this.scope.recommendationsTrainerScheduleViewModel)));

    if (settings.guestEnabled !== undefined) {
      promises.push(this.ServerSettings.updateBoolean(booleanNames.guestEnabled, settings.guestEnabled));
    }

    // Need to set before saving since we copy back the new server settings before the settingsCoordinate save resolve
    this.languageOrLocaleChanged = this.isLangOrLocaleChanged();

    return this.$q.all(promises)
      .then(() => {
        this.masterSettings = settings;
        this.originalActiveDirectorySyncScheduleViewModel = angular.copy(this.scope.activeDirectorySyncScheduleViewModel);
        this.originalRecommendationsTrainerScheduleViewModel = angular.copy(this.scope.recommendationsTrainerScheduleViewModel);

        //client update of token count to get rid of warning text
        if(!settings.refreshTokenEnabled) {
          this.scope.totalRefreshTokenCount = 0;
        }
        ServerSettingsUnauthenticated.fetchServerSettingsUnauthenticated();
      })
      .catch((error: IError) => this.$q.reject(error))
      .finally(() => this.scope.serverSettingsPending = false);
  }

  private runActiveDirectoryGroupsSync(): void {
    this.confirmActionDialog.open({
      title: this.$translate.instant('activeDirectorySyncScheduleConfirmTitle'),
      bodyText: this.$translate.instant('activeDirectorySyncScheduleConfirmBody'),
      actionName: this.$translate.instant('activeDirectorySyncScheduleConfirmButton'),
      action: () => {
        return WebHttpClient.sendRequest(new RunActiveDirectoryGroupsSyncRequest());
      }
    }).result.then((result: IResult) => {
      if (ResultHelper.hasErrors(result)) {
        this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('activeDirectorySyncScheduleRunNowError') });
      } else {
        this.toasterService.pop({ type: ToastType.Info, text: this.$translate.instant('activeDirectorySyncScheduleRunNowSuccess') });
      }
    });
  }

  /**
   * Returns the start page the server admin sets or the default one
   */
  private getServerStartPage(startPage: string): string {
    return startPage ? startPage : StartPageService.getServerDefaultStartPage();
  }

  private resetServerStartPage(): void {
    UserSettingsActions.confirmResetServerDefaultStartPage((newStartPage: string) => {
      this.scope.startPage = newStartPage;
    });
  }

  private runRecommendationsTrainer(): void {
    this.confirmActionDialog.open({
      title: this.$translate.instant('recommendationsTrainerScheduleConfirmTitle'),
      bodyText: this.$translate.instant('recommendationsTrainerScheduleConfirmBody'),
      actionName: this.$translate.instant('recommendationTrainerScheduleConfirmButton'),
      action: () => {
        const requestPromises = [
          WebHttpClient.sendRequest(new RunRecommendationsTrainerRequest()),
          (FeatureFlags.vizRecommendationsEnabled && this.scope.isVizRecommendationsEnabled)
            ? WebHttpClient.sendRequest(new RunViewRecommendationsTrainerRequest())
            : null
        ];
        return Promise.all(_.compact(requestPromises));
      }
    }).result.then(([recTrainerResult, vizRecTrainerResult]: [IResult, IResult]) => {
      if (ResultHelper.hasErrors(recTrainerResult) || ResultHelper.hasErrors(vizRecTrainerResult)) {
        let errorCodes = ResultHelper.getErrorCodes(vizRecTrainerResult);
        if (errorCodes.contains(CommonErrorCode.InProgress)) {
          this.toasterService.pop({ type: ToastType.Error, text: Messages.recommendationsTrainerSchedulerAlreadyRunning() });
        } else {
          this.toasterService.pop({ type: ToastType.Error, text: Messages.recommendationsTrainerScheduleRunNowError() });
        }
      } else {
        this.toasterService.pop({ type: ToastType.Info, text: Messages.recommendationsTrainerScheduleRunNowSuccess() });
      }
    });
  }

  private isLangOrLocaleChanged(): boolean {
    const langChanged = this.scope.serverSettingsOptions.defaultLanguage !== this.masterSettings.defaultLanguage;
    const localeChanged = this.scope.serverSettingsOptions.defaultLocale !== this.masterSettings.defaultLocale;
    return langChanged || localeChanged;
  }

  private hardReloadIfNeeded(): ng.IPromise<any> {
    let hardReloadPromise: ng.IPromise<any>;
    if (this.languageOrLocaleChanged) {
      this.languageOrLocaleChanged = false;
      hardReloadPromise = this.server.refreshSessionInfo().then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));
    }
    return this.$q.when(hardReloadPromise);
  }
}

Module.instance().controller('ServerSettingsFormCtrl', ServerSettingsFormCtrl);
